/**
 * Validation utilities for the hobby fitness app
 * Simple, pragmatic validation following CLAUDE.md principles
 */

/**
 * Validates an API key format (UUID v4 format).
 * Expected format: xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx
 * Example: f5d5b62b-f985-4073-b458-01f9c46f044b
 */
export const validateApiKey = (key: string): boolean => {
  if (!key || typeof key !== 'string') {
    return false
  }

  // UUID v4 regex pattern
  const uuidV4Regex = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i

  return uuidV4Regex.test(key.trim())
}

/**
 * Simple workout validation for API responses
 */
export const validateWorkout = (data: unknown): boolean => {
  return (
    data !== null &&
    typeof data === 'object' &&
    data !== undefined &&
    'id' in data &&
    'title' in data &&
    'start_time' in data
  )
}

/**
 * Simple routine validation for API responses
 */
export const validateRoutine = (data: unknown): boolean => {
  return (
    data !== null &&
    typeof data === 'object' &&
    data !== undefined &&
    'id' in data &&
    'title' in data
  )
}

/**
 * Safe string sanitization
 */
export const sanitizeString = (str: unknown): string => {
  return typeof str === 'string' ? str.trim() : ''
}

/**
 * Safe number sanitization
 */
export const sanitizeNumber = (num: unknown): number => {
  const parsed = Number(num)
  return isNaN(parsed) ? 0 : parsed
}

/**
 * Simple array validation
 */
export const validateArray = (data: unknown): boolean => {
  return Array.isArray(data)
}

/**
 * Simple CSV row validation
 */
export type CSVRowType = {
  [key: string]: string
}

export const CSVRowSchema = {
  validate: (data: unknown): data is CSVRowType => {
    return !!(data && typeof data === 'object' && !Array.isArray(data))
  }
}
